import { Anchor } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { BiDashboardOut } from '@repo/api-codegen';
import { fetchApiCreateBiDashboard } from '@repo/api-codegen';
import { apiListTeamsV2QueryKey } from '@repo/api-codegen/v2';
import { iconEmojiSelectorValueToString } from '@repo/common/components/IconEmojiSelector/utils';
import { EntityType } from '@repo/common/enums/entityType';
import { Icon } from '@repo/foundations';
import type { AxiosResponse } from 'axios';
import { makeAutoObservable } from 'mobx';
import type { NavigateFunction } from 'react-router';
import type { IEmbeddedPrompt, ISecodaEntity, ITemplate } from '../../api';
import {
	createCollection,
	createDocument,
	createMetric,
	createQuestion,
	createSecodaEntity,
	queryClient,
} from '../../api';
import { createResourceRelation } from '../../api/hooks/relations';
import { createQuestionEntityRelation } from '../../api/hooks/relations/useQuestionEntityRelation';
import { resourceCatalogQueryKeyFactory } from '../../api/hooks/resourceCatalog/constants';
import type { APIListResponseData } from '../../lib/models';
import { search } from '../../lib/models';
import { mapEntityTypeToIcon } from '../../utils/integrationLogo';
import { ENTITY_TYPES_MODAL_CONFIG } from './constants';
import { sanitizeTemplateRichTextField } from './utils';

export interface OpenEntityModalDataRelatedResource {
	id: string;
	teams?: string[];
	integration?: string;
	owners?: string[];
	owners_groups?: string[];
}
export interface OpenEntityModalData {
	type?: EntityType;
	relatedResource?: OpenEntityModalDataRelatedResource;
	teamId?: string;
	teamIds?: string[];
	template?: ITemplate;
	metadata?: Record<string, unknown>;
	title?: string;
	description?: string;
	withLinkToAI?: boolean;
}

const invalidateTeamEmptyStateQuery = (teamIds: string[]) => {
	// Invalidate teams list query (which includes empty states for all teams)
	if (teamIds.length > 0) {
		queryClient.invalidateQueries({
			queryKey: apiListTeamsV2QueryKey({}),
		});
	}
};

class EntityModalStore {
	type: EntityType | undefined;

	relatedResource?: OpenEntityModalDataRelatedResource;

	question?: ISecodaEntity;

	template: ITemplate | undefined;

	loading = false;

	opened = false;

	title = '';

	description = '';

	metadata: Record<string, unknown> = {};

	relatedQuestions: ISecodaEntity[] | undefined;

	loadingRelatedQuestions = false;

	// Warning: deprecated attribute. We should be able to support multiple teams in the future.
	// Currently only questions support multiple teams.
	teamId?: string;

	teamIds: string[] | undefined;

	withLinkToAI = false;

	triedToCreate = false;

	relatedResourcesFromTemplate?: string[];

	constructor() {
		makeAutoObservable(this);
	}

	open = async (data: OpenEntityModalData) => {
		this.reset();

		this.type = data.type;
		this.teamId = data.teamId;
		this.teamIds = data.teamIds;
		this.relatedResource = data.relatedResource;
		this.metadata = data.metadata ?? {};
		this.title = data.title ?? '';
		this.description = data.description ?? '';
		this.withLinkToAI = data.withLinkToAI ?? false;
		if (data.template) {
			const owner =
				Array.isArray(data.metadata?.owners) &&
				data.metadata.owners.length > 0 &&
				typeof data.metadata.owners[0] === 'string'
					? data.metadata.owners[0]
					: undefined;

			await this.setTemplate(data.template, undefined, data.teamId, owner);
		}

		// open it after all properties have been set
		this.opened = true;
	};

	close() {
		this.opened = false;
	}

	reset() {
		this.type = undefined;
		this.relatedResource = undefined;
		this.template = undefined;
		this.loading = false;
		this.opened = false;
		this.title = '';
		this.description = '';
		this.metadata = {};
		this.teamId = undefined;
		this.relatedQuestions = undefined;
		this.loadingRelatedQuestions = false;
		this.teamIds = undefined;
		this.withLinkToAI = false;
		this.triedToCreate = false;
	}

	setTitle = (title: string) => {
		this.title = title;
	};

	setTemplate = async (
		template: ITemplate,
		type?: EntityType,
		currentTeamId?: string,
		currentUserId?: string
	) => {
		this.template = template;
		if (type) {
			this.type = type;
		}

		// this template might be applied to the description or the definition field, depending on the entity type
		const templateRichTextValue =
			this.type &&
			template?.[
				ENTITY_TYPES_MODAL_CONFIG[this.type].richTextTemplateField ??
					'definition'
			];

		// every time a template is used, we need to refresh the query blocks IDs to make sure they are unique per entity
		const finalDescription = await sanitizeTemplateRichTextField(
			templateRichTextValue ?? ''
		);
		this.setDescription(finalDescription);

		const teamIds = [...(template.teams ?? [])];
		// add the current teamId so that entities do not get 'lost'
		if (currentTeamId && !teamIds.includes(currentTeamId)) {
			teamIds.push(currentTeamId);
		}
		this.teamIds = teamIds;

		const owners = [...(template.owners ?? [])];
		if (currentUserId && !owners.includes(currentUserId)) {
			owners.push(currentUserId);
		}
		this.setMetadata('owners', owners);

		this.setTitle(template.title ?? '');
		this.setMetadata('icon', template.icon ?? '');
		this.setMetadata('parent', template.parent ?? '');
		this.setMetadata('published', template.published ?? false);
		this.setMetadata('assigned_to', template.assigned_to ?? '');
		this.setMetadata('assigned_to_group', template.assigned_to_group ?? '');
		this.setMetadata('priority', template.priority ?? '');
		this.setMetadata('tags', template.tags ?? []);
		this.setMetadata('collections', template.collections ?? []);
		this.setMetadata('properties2', template.properties2 ?? {});
		this.setMetadata('synonyms', template.synonyms ?? []);
		this.relatedResourcesFromTemplate = template.related_resources ?? [];
		if (
			template.entity_type_template === EntityType.question &&
			template.status
		) {
			this.setMetadata('status', template.status);
		}

		// This is used to force re-rendering of the component when the template changes
		this.setMetadata('readyId', template.id);
	};

	setDescription = (description: string) => {
		this.description = description;
	};

	setMetadata = (key: string, value: unknown) => {
		this.metadata[key] = value;
	};

	setWithLinkToAI = (value: boolean) => {
		this.withLinkToAI = value;
	};

	checkEmptyTitle() {
		if (this.title === '') {
			this.triedToCreate = true;
			showNotification({
				title: 'Title required',
				message: 'Please add a title before creating',
			});
			this.loading = false;
			return true;
		}
		return false;
	}

	findRelatedQuestions = () => {
		this.loadingRelatedQuestions = true;
		search(
			{ search_term: this.title, page: 1, entity_type: EntityType.question },
			false
		)
			.then((res: AxiosResponse<APIListResponseData>) => {
				if (res.data.results.length > 0) {
					this.relatedQuestions = res.data.results.slice(0, 5); // Get max 5 related questions
				}
				this.loadingRelatedQuestions = false;
			})
			.catch(() => {
				this.loadingRelatedQuestions = false;
			});
	};

	async quickCreateEntity(
		entityType: EntityType,
		title: string,
		teamIds: Array<string> | undefined,
		metadata: Record<string, unknown>,
		navigate: NavigateFunction,
		template?: ITemplate
	) {
		this.type = entityType;
		this.title = title;
		this.teamIds = teamIds;
		this.metadata = metadata;

		if (template) {
			const currentUserId =
				Array.isArray(metadata?.owners) &&
				metadata.owners.length > 0 &&
				typeof metadata.owners[0] === 'string'
					? (metadata.owners[0] as string)
					: undefined;
			await this.setTemplate(template, undefined, teamIds?.[0], currentUserId);
		}

		await this.create(navigate, true, true);
	}

	async create(
		navigate: NavigateFunction,
		navigateToEntity: boolean = false,
		allowEmptyTitle: boolean = false,
		onEntityCreated?: (entityType: EntityType, entityId: string) => void
	) {
		this.loading = true;

		if (!allowEmptyTitle && this.checkEmptyTitle()) {
			return;
		}

		switch (this.type) {
			case EntityType.question:
				await this.createQuestion(navigate, navigateToEntity, allowEmptyTitle);
				break;
			case EntityType.collection:
				await this.createCollection(
					navigate,
					undefined,
					navigateToEntity,
					allowEmptyTitle
				);
				break;
			case EntityType.document:
				this.createDocument(navigate, navigateToEntity, allowEmptyTitle);
				break;
			case EntityType.metric:
				this.createMetric(navigate, navigateToEntity, allowEmptyTitle);
				break;
			case EntityType.glossary:
				this.createGlossaryTerm(navigate, navigateToEntity, allowEmptyTitle);
				break;
			case EntityType.bi_dashboard:
				this.createBiDashboard(
					navigate,
					navigateToEntity,
					allowEmptyTitle,
					onEntityCreated
						? (id: string) => onEntityCreated(EntityType.bi_dashboard, id)
						: undefined
				);
				break;
			default:
				break;
		}
	}

	private showErrorNotification(entityType: string) {
		showNotification({
			title: `Error creating ${entityType.toLowerCase()}`,
			message: 'If the issue persists, please contact support',
			color: 'red',
			icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
		});
	}

	createQuestion = async (
		navigate: NavigateFunction,
		navigateToEntity: boolean = false,
		allowEmptyTitle: boolean = false
	) => {
		this.loading = true;

		if (!allowEmptyTitle && this.checkEmptyTitle()) {
			return;
		}

		const status = (this.metadata.status ?? 'UNANSWERED') as string;
		const owners = (this.metadata?.owners ?? []) as string[];
		const assignedTo = (this.metadata.assigned_to ?? null) as
			| string
			| undefined;
		const assignedToGroup = (this.metadata.assigned_to_group ?? null) as
			| string
			| undefined;
		const priority = (this.metadata.priority ?? null) as string | undefined;
		const tags = (this.metadata?.tags ?? []) as string[];
		const aiPrompt = (this.metadata.ai_prompt ?? null) as
			| IEmbeddedPrompt
			| undefined;
		const teams: string[] = [];

		if (this.teamIds && this.teamIds.length > 0) {
			teams.push(...this.teamIds);
		} else if (this.teamId) {
			// Legacy support for single team questions.
			teams.push(this.teamId);
		}

		createQuestion({
			data: {
				title: this.title,
				definition: this.description,
				status,
				owners,
				assigned_to: assignedTo,
				assigned_to_group: assignedToGroup,
				priority,
				tags,
				teams,
				properties: this.template?.properties,
				properties2: this.template?.properties2,
				ai_prompt: this.withLinkToAI ? aiPrompt?.id : undefined,
				subscribers: this.relatedResource?.owners,
				subscribers_groups: this.relatedResource?.owners_groups,
			},
		})
			.then(async (question: ISecodaEntity) => {
				this.question = question;
				if (this.relatedResource) {
					try {
						await createQuestionEntityRelation({
							questionId: question.id,
							entityId: this.relatedResource.id,
						});
					} catch {
						showNotification({
							title: 'Failed to create resource relation',
							message:
								'The question was created but could not be linked to the resource',
							color: 'yellow',
						});
					}
				}

				if (this.relatedResourcesFromTemplate) {
					await Promise.all(
						this.relatedResourcesFromTemplate.map((resourceId) =>
							createResourceRelation({
								data: {
									from_entity: resourceId,
									to_entity: question.id,
								},
							})
						)
					);
				}

				if (navigateToEntity) {
					navigate(`/questions/${question.id}`);
				}

				// Invalidate team empty state query
				invalidateTeamEmptyStateQuery(teams);

				// Invalidate resource catalog lists so new questions appear without refresh
				queryClient.invalidateQueries({
					queryKey: resourceCatalogQueryKeyFactory.allLists(),
				});

				this.reset();

				showNotification({
					title: 'Question created',
					message: (
						<Anchor
							data-testid="created-notification"
							role="link"
							onClick={() => navigate(`/questions/${question.id}`)}
						>
							View your question
						</Anchor>
					),
					color: 'green',
				});
			})
			.catch(() => this.showErrorNotification('question'))
			.finally(() => {
				this.loading = false;
			});
	};

	createCollection = async (
		navigate: NavigateFunction,
		addToCollectionById?: (
			collectionId: string,
			entityId: string
		) => Promise<void>,
		navigateToEntity: boolean = false,
		allowEmptyTitle: boolean = false
	) => {
		this.loading = true;

		if (!allowEmptyTitle && this.checkEmptyTitle()) {
			return;
		}

		const owners = (this.metadata?.owners ?? []) as string[];
		const tags = (this.metadata?.tags ?? []) as string[];
		const teams: string[] = [];
		if (this.teamIds && this.teamIds.length > 0) {
			teams.push(...this.teamIds);
		} else if (this.teamId) {
			// Legacy support for single team.
			teams.push(this.teamId);
		}
		const collections = (
			this.metadata.parent ? [this.metadata.parent] : []
		) as string[];

		await createCollection({
			data: {
				title: this.title,
				description: this.description,
				icon:
					(this.metadata.icon as string) ||
					iconEmojiSelectorValueToString(
						mapEntityTypeToIcon(EntityType.collection)!
					),
				owners,
				published: this.metadata.published as boolean,
				pinned: this.metadata.pinned as boolean,
				teams,
				tags,
				collections,
				properties: this.template?.properties,
				properties2: this.template?.properties2,
			},
		})
			.then(async (collection: ISecodaEntity) => {
				if (this.relatedResourcesFromTemplate && addToCollectionById) {
					await Promise.all(
						this.relatedResourcesFromTemplate.map((resourceId) =>
							addToCollectionById(collection.id, resourceId)
						)
					);
				}

				showNotification({
					title: 'Collection created',
					message: (
						<Anchor
							data-testid="created-notification"
							role="link"
							onClick={() => navigate(`/collections/${collection.id}`)}
						>
							{collection.title}
						</Anchor>
					),
					color: 'green',
				});

				if (navigateToEntity) {
					navigate(`/collections/${collection.id}`);
				}

				// Reload the table v2 and data tree
				queryClient.invalidateQueries({
					queryKey: resourceCatalogQueryKeyFactory.all(),
				});

				// Invalidate team empty state query
				invalidateTeamEmptyStateQuery(teams);

				this.reset();
			})
			.catch(() => this.showErrorNotification('collection'))
			.finally(() => {
				this.loading = false;
			});
	};

	createDocument = async (
		navigate: NavigateFunction,
		navigateToEntity: boolean = false,
		allowEmptyTitle: boolean = false
	) => {
		this.loading = true;

		if (!allowEmptyTitle && this.checkEmptyTitle()) {
			return;
		}

		const owners = (this.metadata?.owners ?? []) as string[];
		const collections = (this.metadata?.collections ?? []) as string[];
		const tags = (this.metadata?.tags ?? []) as string[];
		const published = (this.metadata.published ?? false) as boolean;

		const teams: string[] = [];
		if (this.teamIds && this.teamIds.length > 0) {
			teams.push(...this.teamIds);
		} else if (this.teamId) {
			// Legacy support for single team.
			teams.push(this.teamId);
		}

		createDocument({
			data: {
				title: this.title,
				definition: this.description,
				icon:
					(this.metadata.icon as string) || 'iconName:file iconColor:#4a4a4a',
				published,
				teams,
				owners,
				collections,
				tags,
				properties: this.template?.properties,
				properties2: this.template?.properties2,
			},
		})
			.then(async (document: ISecodaEntity) => {
				if (this.relatedResourcesFromTemplate) {
					await Promise.all(
						this.relatedResourcesFromTemplate.map((resourceId) =>
							createResourceRelation({
								data: {
									from_entity: resourceId,
									to_entity: document.id,
								},
							})
						)
					);
				}

				showNotification({
					title: 'Document created',
					message: (
						<Anchor
							data-testid="created-notification"
							role="link"
							onClick={() => navigate(`/docs/${document.id}`)}
						>
							{document.title}
						</Anchor>
					),
					color: 'green',
				});

				if (navigateToEntity) {
					navigate(`/docs/${document.id}`);
				}

				// Reload the table v2 and data tree
				queryClient.invalidateQueries({
					queryKey: resourceCatalogQueryKeyFactory.all(),
				});

				// Invalidate team empty state query
				invalidateTeamEmptyStateQuery(teams);

				this.reset();
			})
			.catch(() => this.showErrorNotification('document'))
			.finally(() => {
				this.loading = false;
			});
	};

	createMetric = (
		navigate: NavigateFunction,
		navigateToEntity: boolean = false,
		allowEmptyTitle: boolean = false
	) => {
		this.loading = true;
		if (!allowEmptyTitle && this.checkEmptyTitle()) {
			return;
		}

		const owners = (this.metadata?.owners ?? []) as string[];
		const collections = (this.metadata?.collections ?? []) as string[];
		const tags = (this.metadata?.tags ?? []) as string[];

		const teams: string[] = [];
		if (this.teamIds && this.teamIds.length > 0) {
			teams.push(...this.teamIds);
		} else if (this.teamId) {
			// Legacy support for single team.
			teams.push(this.teamId);
		}

		createMetric({
			data: {
				title: this.title,
				description: this.description,
				teams,
				owners,
				collections,
				tags,
				properties: this.template?.properties,
				properties2: this.template?.properties2,
			},
		})
			.then(async (metric: ISecodaEntity) => {
				showNotification({
					title: 'Metric created',
					message: (
						<Anchor
							data-testid="created-notification"
							role="link"
							onClick={() => navigate(`/metrics/${metric.id}`)}
						>
							{metric.title}
						</Anchor>
					),
					color: 'green',
				});

				if (navigateToEntity) {
					navigate(`/metrics/${metric.id}`);
				}

				// Reload the table v2 and data tree
				queryClient.invalidateQueries({
					queryKey: resourceCatalogQueryKeyFactory.all(),
				});

				// Invalidate team empty state query
				invalidateTeamEmptyStateQuery(teams);

				this.reset();
			})
			.catch(() => this.showErrorNotification('metric'))
			.finally(() => {
				this.loading = false;
			});
	};

	createGlossaryTerm = async (
		navigate: NavigateFunction,
		navigateToEntity: boolean = false,
		allowEmptyTitle: boolean = false
	) => {
		this.loading = true;

		if (!allowEmptyTitle && this.checkEmptyTitle()) {
			return;
		}

		const owners = (this.metadata?.owners ?? []) as string[];
		const collections = (this.metadata?.collections ?? []) as string[];
		const tags = (this.metadata?.tags ?? []) as string[];
		const synonyms = (this.metadata?.synonyms ?? []) as string[];
		const published = (this.metadata.published ?? false) as boolean;

		const teams: string[] = [];
		if (this.teamIds && this.teamIds.length > 0) {
			teams.push(...this.teamIds);
		} else if (this.teamId) {
			// Legacy support for single team.
			teams.push(this.teamId);
		}

		createSecodaEntity({
			data: {
				entity_type: EntityType.glossary,
				title: this.title,
				description: this.description,
				icon:
					(this.metadata.icon as string) || 'iconName:book iconColor:#4a4a4a',
				definition: await sanitizeTemplateRichTextField(
					this.template?.definition ?? ''
				),
				published,
				teams,
				owners,
				collections,
				tags,
				properties: this.template?.properties,
				properties2: this.template?.properties2,
				synonyms,
			},
		})
			.then(async (glossaryTerm: ISecodaEntity) => {
				if (this.relatedResourcesFromTemplate) {
					await Promise.all(
						this.relatedResourcesFromTemplate.map((resourceId) =>
							createResourceRelation({
								data: {
									from_entity: resourceId,
									to_entity: glossaryTerm.id,
								},
							})
						)
					);
				}

				showNotification({
					title: 'Glossary term created',
					message: (
						<Anchor
							data-testid="created-notification"
							role="link"
							onClick={() => navigate(`/glossary/${glossaryTerm.id}`)}
						>
							{glossaryTerm.title}
						</Anchor>
					),
					color: 'green',
				});

				if (navigateToEntity) {
					navigate(`/glossary/${glossaryTerm.id}`);
				}

				// Reload the table v2 and data tree
				queryClient.invalidateQueries({
					queryKey: resourceCatalogQueryKeyFactory.all(),
				});

				// Invalidate team empty state query
				invalidateTeamEmptyStateQuery(teams);

				this.reset();
			})
			.catch(() => this.showErrorNotification('glossary term'))
			.finally(() => {
				this.loading = false;
			});
	};

	createBiDashboard = async (
		navigate: NavigateFunction,
		navigateToEntity: boolean = true,
		allowEmptyTitle: boolean = false,
		onCreated?: (dashboardId: string) => void
	) => {
		this.loading = true;
		if (!allowEmptyTitle && this.checkEmptyTitle()) {
			return;
		}

		const owners = (this.metadata?.owners ?? []) as string[];
		const collections = (this.metadata?.collections ?? []) as string[];
		const tags = (this.metadata?.tags ?? []) as string[];

		const teams: string[] = [];
		if (this.teamIds && this.teamIds.length > 0) {
			teams.push(...this.teamIds);
		} else if (this.teamId) {
			// Legacy support for single team.
			teams.push(this.teamId);
		}

		fetchApiCreateBiDashboard({
			body: {
				name: this.title,
				owners,
				collections,
				tags,
				teams,
			},
		}).then(async (biDashboard: BiDashboardOut) => {
			showNotification({
				title: 'Dashboard created',
				message: (
					<Anchor
						data-testid="created-notification"
						role="link"
						onClick={() => navigate(`/bi/dashboards/${biDashboard.id}`)}
					>
						{biDashboard.secoda_entity.title}
					</Anchor>
				),
				color: 'green',
			});

			if (navigateToEntity) {
				navigate(`/bi/dashboards/${biDashboard.id}`);
			}

			onCreated?.(biDashboard.id);

			queryClient.invalidateQueries({
				queryKey: resourceCatalogQueryKeyFactory.all(),
			});

			invalidateTeamEmptyStateQuery(teams);

			this.reset();
		});
	};

	get titleValidationError() {
		if (this.title === '' && this.triedToCreate) {
			return 'Title is required';
		}

		if (this.title.length > 280) {
			return 'Title must be less than 280 characters';
		}

		return null;
	}
}

export const entityModalStore = new EntityModalStore();
